import React, { useState } from "react";

// 21 Jul 2025
import { useGetUsersQuery } from "@/features/users/usersApiSlice";
import EtImprintGratitude from "@/pages/home/web/userImprint/ImprintGratitudes/EtImprintGratitude";

import EtTrinityGuidedGratitudeFlow from "@/components/ai/EtTrinityGuidedGratitudeFlow"

import { useCreateNewUserGratitudeImprintMutation } from '@/features/userGratitudeImprints/userGratitudeImprintsApiSlice'
import { useGetContactTypesQuery } from '@/features/contactTypes/contactTypesApiSlice'
import { useGetGratitudeTypesQuery } from '@/features/gratitudeTypes/gratitudeTypesApiSlice'
import { useGetContactsQuery } from '@/features/contacts/contactsApiSlice'

const captureGratitudePrompts = [
  { step: "gratitudeTarget1", prompt: "Who would you like to extend your gratitude? Is this for a specific person?" },
  
  { step: "gratitudeTarget2a", prompt: "Great! please start typing the name of the person", when:"this step is activated when the user types yes for the first one" },
  
  { step: "gratitudeTarget2b", prompt: "Great! we are now ready to create a gratitude. Please tell me what this gratitude is for.", when:"this step is activated when the user types yes for the first one and types in a name which matches the db"},

  { step: "gratitudeTarget2c", prompt: "I am sorry, but I cannot find a contact with that name. Please go ahead and create this contact first and then come back here or try typing again. We will get cracking then!", when:"this step is activated when the user types yes for the first one and types in a name which doesn't match any name"},

  { step: "gratitudeTarget3a", prompt: "Great!. Please specify a group of people to whom you would extend your gratitude. These could be your clients, partners, prospects.", when:"this step is activated when the user types no or types in a group for the first one and an exact match is found" },
  
  { step: "gratitudeTarget3b", prompt: "Super!, we are now ready for capturing your gratitude.Please tell me what this gratitude is for.", when:"this step is activated when the user types no or types in a group in step 3a and an exact match is found" },

  { step: "gratitudeTarget3c", prompt: "I am sorry but that group doesn't exist. Here are the groups which you can select from. Select one which best matches this gratitude", when:"this step is activated when the user types in a group in step 3a and no match is found" },



  { step: "gratitudeMsg", prompt: "Please describe in a few words what are you thankful for" },

  { step: "impact", prompt: "How their help has benefited or impacted you?" },

  { step: "feeling", prompt: "How do you feel about this as a result?" },

  { step: "reciprocation", prompt: "How would you like to reciprocate to them?" },

  { step: "finalize", prompt: "Super! Based on your thoughts, I’ll now craft your gratitude draft. Ready?" },
];

const EtTrinitySendGratitude = ({ userId, onClose }) => {
  
  // First Created - 19 May 2025
  // Author - Atul Pandey
  // Purpose - Prompt user to write a gratitude note
  // Updated - 21 Jul 2025 - added the main logic - classic and Trinity way

  // 1 - Define State Variables
  const [viewTab, setViewTab] = useState("aiCapture");
  const [responses, setResponses] = useState({});

  // 2a - Define RTK mutations and queries
  const [createNewUserGratitudeImprint, {
    isLoading,
    isSuccess,
    isError,
    error,
  }] = useCreateNewUserGratitudeImprintMutation();

  // 2b - Get UserInfo
  const {data: usersData } = useGetUsersQuery()
  const userInfo = usersData?.ids.map(id => usersData?.entities[id])?.filter(user => user._id === userId)
  const userOrgId = userInfo[0]?.userOrgId?._id

  // console.log('userInfo', userInfo)
  // console.log('userOrgId', userOrgId)

  // 2c - Get Data for building User Gratitude Imprints
  const { data: contactTypesData} = useGetContactTypesQuery()
  const { data: gratitudeTypesData} = useGetGratitudeTypesQuery()
  const { data: contactsData} = useGetContactsQuery()

  const extractEntitiesFromAdapter = (dataAdapter) => {
    if (!dataAdapter || !dataAdapter.ids || !dataAdapter.entities) return [];
    return dataAdapter.ids.map((id) => dataAdapter.entities[id]);
  };

  const contactTypes = extractEntitiesFromAdapter(contactTypesData);
  const gratitudeTypes = extractEntitiesFromAdapter(gratitudeTypesData);
  const contacts = extractEntitiesFromAdapter(contactsData);

  // 2d: Filter contacts for the given userId
  const filteredContacts = contacts?.filter((contact) => {
    const isOwner = contact.contactOwnerId._id === userId; // Check if the contactOwnerId matches the userId
    const isSharedWithUser = contact.sharedWith.some(
      (shared) => shared.userId._id === userId // Check if userId is in the sharedWith array
    );
    return isOwner || isSharedWithUser; // Return true if either condition is met
  });
  
  console.log('filteredContacts', filteredContacts);

  // 3 - Handler to submit gratitude as a userGratitudeImprint
  const handleFinalGratitudeResponse = async (finalResponse) => {
    try {
      const {
        userGratitudeNoteText,
        userGratitudeTypeId,
        userGratitudeTargetId,
        userGratitudeFor,
        ...userResponses
      } = finalResponse;

      const payload = {
        userId,
        userOrgId,
        userGratitudeTypeId,
        userGratitudeTargetId: userGratitudeTargetId || null,
        userGratitudeFor: userGratitudeFor || null,
        userGratitudeNoteText,
        userGratitudeAIContext: userResponses,
      };

      console.log("Final Gratitude Payload:", payload);
      await createNewUserGratitudeImprint(payload).unwrap();
      alert("Your gratitude has been saved!");
    } catch (error) {
      console.error("❌ Error saving AI-generated gratitude:", error);
      alert("There was an error saving your gratitude.");
    }
  };

  
  return (
    <div className="p-4">
      
      <div className="flex justify-between items-center m-1 p-1">
          <p className="text-lg font-bold text-gray-800 mb-2">Capture a Gratitude </p>
          <button onClick={onClose} className="mt-4 text-sm text-blue-600 underline">Close</button>
      </div>

      <div className="flex space-x-4 border-b border-gray-300 mb-4">
        <button
          onClick={() => setViewTab("aiCapture")}
          className={`text-sm font-semibold px-3 py-1 ${
            viewTab === "aiCapture"
              ? "border-b-2 border-orange-600 text-orange-800 bg-orange-200 rounded-t"
              : "text-gray-500"
          }`}
        >
          🧠 Trinity Way!
        </button>
        <button
          onClick={() => setViewTab("classicView")}
          className={`text-sm font-semibold px-3 py-1 ${
            viewTab === "classicView"
              ? "border-b-2 border-orange-600 text-orange-800 bg-orange-200 rounded-t"
              : "text-gray-500"
          }`}
        >
          {/* 🧱 Gratitude Wall! */}
          🧱 Classic Way!
        </button>
      </div>

      {viewTab === "aiCapture" && (
        <div className="space-y-4">
          {/* 🧠 Header: Invite user to start capturing thoughts */}
          <div className="bg-white shadow rounded p-4 border border-gray-200 animate-fade-in">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">Start Capturing a Gratitude</h2>
            <p className="text-sm text-gray-800 bg-orange-50 rounded px-2 py-2 my-2 animate-slide-in-left">
              Expressing a gratitude is powerful! However, it may not often be easy to do so as you may be filled with many emotions. I’ll be happy to guide you in capturing one by breaking it down into a few simple parts.
            </p>
          </div>

          {/* ✍️ AI-Guided Gratitude Capture Flow */}
          <EtTrinityGuidedGratitudeFlow 
            context={userInfo}
            promptFlow={captureGratitudePrompts}
            contactTypes={contactTypes}
            gratitudeTypes={gratitudeTypes}
            contacts={filteredContacts}
            onComplete={handleFinalGratitudeResponse}
            
          />
      </div>
      )}

      {viewTab === "classicView" && (
        <EtImprintGratitude userId={userId} userInfo={userInfo} />
      )}

    </div>
  );
};

export default EtTrinitySendGratitude;
